import dynamic from 'next/dynamic';
import styled from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { BuyButtons } from 'productTemplate/components/buyBar/BuyButtons';
import { FactsTable } from 'productTemplate/components/facts/FactsTable';
import { FeaturedInProducts } from 'productTemplate/components/relatedProducts/FeaturedInProducts';
import { FeatureFlags } from 'featureFlags/constants';
import { formatString } from 'productTemplate/utils/formatStringForUrl';
import { getBuyBarDeepLinkQueryParams } from 'productTemplate/utils/getBuyBarDeepLinkQueryParams';
import {
  getProductRobotImages,
  getRobotProductImagesForCarousel,
} from 'productTemplate/utils/getProductRobotImages';
import { getProductTemplateCarouselImages } from 'productTemplate/utils/getProductTemplateCarouselImages';
import { ImageGalleryMobile } from 'productTemplate/components/imageGallery/ImageGalleryMobile';
import { IMobileBuyBarView } from 'store/types/buyBarTypes';
import { IProductTemplate } from 'shared/types/ProductTemplate';
import { IRelatedProductData } from 'productTemplate/types/FactsWindowData';
import { MOBILE_BANNER_HEIGHT } from 'banner/enums/constants';
import { MobileActivityButton } from 'productTemplate/components/windows/MobileActivityInfo';
import { MobileFitInfo } from 'productTemplate/components/fitFeedback/MobileFitInfo';
import { NAV_PDP_MOBILE_HEADER_HEIGHT } from 'layout/header/enums/constants';
import { OffersProvider } from 'productTemplate/providers/OffersProvider';
import { ProductDescription } from 'productTemplate/components/facts/ProductDescription';
import { ProductDetailPageMobileOffersModal } from 'productTemplate/components/ProductDetailPageMobileOffersModal';
import { ProductInfo } from 'productTemplate/components/facts/ProductInfo';
import { RelatedProducts } from 'productTemplate/components/relatedProducts/RelatedProducts';
import { RobotsGalleryMobile } from 'productTemplate/components/robotGallery/RobotsGalleryMobile';
import { sendTrackingPdpView } from 'productTemplate/utils/sendPdpViewTrackingEvent';
import { setExpandedSize, setMobileBuyBarView } from 'store/slice/buyBarSlice';
import { SizeConversionProvider } from 'productTemplate/providers/SizeConversionProvider';
import { t } from 'localization/utils/t';
import { TAB_VIEWS } from 'productTemplate/enums/buyBarMobile';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useIsBannerVisible } from 'shared/hooks/useIsBannerVisible';
import { useMParticleContext } from 'tracking/hooks/useMParticleContext';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { WantProductTemplateButton } from 'wants/components/WantProductTemplateButton';

const Footer = dynamic(() => import('layout/footer/components/Footer'));
const BuyBarAccordionView = dynamic(
  () =>
    import('productTemplate/components/buyBar/BuyBarAccordionView/BuyBarAccordionView').then(
      (mod) => mod.BuyBarAccordionView,
    ),
  {
    ssr: false,
  },
);

const TP = 'productTemplate.components.windows.FactsWindow';

interface IProductDetailPageMobile {
  productTemplate: IProductTemplate;
  recommendedProducts: Array<IRelatedProductData>;
}

export const ProductDetailPageMobile = ({
  productTemplate,
  recommendedProducts,
}: IProductDetailPageMobile) => {
  /* Ref */
  const mainImageObserver = useRef<HTMLDivElement | null>(null);
  /* Hooks */
  const router = useRouter();
  const { asPath, query, pathname } = router;
  const { tab: tabFromQuery, expandedSize: expandedSizeFromQuery } = query;
  const isBannerVisible = useIsBannerVisible();
  const { isMParticleReady } = useMParticleContext();
  /* Feature Flags */
  const enablePdpImgAltTag = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_PDP_IMAGE_ALT_TAG);
  /* Redux Store */
  const {
    brandName,
    id,
    isOfferable,
    isRaffleProduct,
    isResellable,
    name,
    productType,
    releaseDate,
    releaseYear,
    robotAssets,
    silhouette,
    slug,
    specialType,
    story,
  } = productTemplate;
  const storeDispatch = useStoreDispatch();
  const { featuredInCollections, factsWindowData } = useStoreSelector((state) => state.pdp);
  const { brandProducts, categoryProducts, silhouetteProducts } = factsWindowData;
  const { mobileBuyBarView, expandedSize } = useStoreSelector((state) => state.buyBar);
  const { isMobileSearchOpen, isMenuVisible } = useStoreSelector((state) => state.pageLayout);
  /* Local State */
  const [innerHeight, setInnerHeight] = useState(0);
  /* Utils */
  const isBuyBarOpen = mobileBuyBarView !== 'none';
  const productTemplateImages = getProductTemplateCarouselImages({
    productTemplate,
    enablePdpImgAltTag,
  });
  const showPricingActivity =
    isOfferable && specialType === 'standard' && isResellable && !isRaffleProduct;
  const prevPath =
    typeof sessionStorage !== 'undefined' ? sessionStorage?.getItem('prevPath') : null;
  const currentPath =
    typeof sessionStorage !== 'undefined' ? sessionStorage?.getItem('currentPath') : null;

  useEffect(() => {
    // prevents the page from retaining the scroll position on back button navigation
    if (window) {
      window.history.scrollRestoration = 'manual';
      setInnerHeight(window.innerHeight);
    }

    if (tabFromQuery && expandedSizeFromQuery) {
      storeDispatch(setExpandedSize(expandedSizeFromQuery as string));
      storeDispatch(setMobileBuyBarView(tabFromQuery as IMobileBuyBarView));
    }
  }, []);

  useEffect(() => {
    // Close the buy bar when changing PT's if there is no tab or expandedSize query
    if (!tabFromQuery || !expandedSizeFromQuery) {
      storeDispatch(setMobileBuyBarView(TAB_VIEWS.NONE));
    }
  }, [id]);

  useEffect(() => {
    if (expandedSize && mobileBuyBarView) {
      const queryParams = getBuyBarDeepLinkQueryParams({ query, mobileBuyBarView, expandedSize });

      // Add the expanded size and tab to the query params to allow for deeplinking
      void router.replace({ pathname: asPath.split('?')?.[0], query: queryParams });
    }
  }, [mobileBuyBarView, expandedSize]);

  useEffect(() => {
    // on initial page load the prevPath is still the currentPath so wait until currentPath is asPath
    if (currentPath === asPath && isMParticleReady) {
      sendTrackingPdpView({ prevPath, id, name, slug, brandName, query });
    }
  }, [currentPath, prevPath, asPath, isMParticleReady, id, query, pathname, name, slug, brandName]);

  return (
    <ProductDetailPageMobile.Wrapper
      $isBannerVisible={isBannerVisible}
      $innerHeight={innerHeight}
      className={isBuyBarOpen ? 'lockScroll' : ''}
    >
      <div className="topGradient" />
      {!isMobileSearchOpen && !isMenuVisible && (
        <div className="wantsButtonWrapper">
          <WantProductTemplateButton />
        </div>
      )}
      <div className="mainView">
        <ImageGalleryMobile itemList={productTemplateImages} ref={mainImageObserver} />
        <OffersProvider>
          <SizeConversionProvider>
            <ProductDetailPageMobileOffersModal />
            {isBuyBarOpen && <BuyBarAccordionView />}
            <div className="productInfoFit">
              <ProductInfo name={name} releaseDate={releaseDate} releaseYear={releaseYear} />
              <div className="activityAndFitButtons">
                {showPricingActivity && <MobileActivityButton />}
                <MobileFitInfo />
              </div>
            </div>
            <BuyButtons />
          </SizeConversionProvider>
        </OffersProvider>
      </div>
      {story && <ProductDescription text={story} />}
      {robotAssets?.length > 0 && (
        <RobotsGalleryMobile
          productTemplateImages={getRobotProductImagesForCarousel({ robotAssets, name })}
          robotAssets={getProductRobotImages({ robotAssets, cutOff: 5 })}
          name={name}
        />
      )}
      {silhouetteProducts?.length > 0 && (
        <RelatedProducts
          productTemplateSlug={slug}
          title={silhouette}
          titleLink={`/sneakers/silhouette/${formatString(silhouette)}`}
          products={silhouetteProducts}
          section="sku_name"
        />
      )}
      {brandProducts?.length > 0 && (
        <RelatedProducts
          productTemplateSlug={slug}
          title={brandName}
          titleLink={`/brand/${formatString(brandName)}`}
          products={brandProducts}
          section="brand_name"
        />
      )}
      {categoryProducts?.length > 0 && (
        <RelatedProducts
          productTemplateSlug={slug}
          title={productType.replace(/_/g, ' ')}
          titleLink={`/apparel/brand/${formatString(brandName)}/${formatString(productType)}`}
          products={categoryProducts}
          section="category"
        />
      )}
      <FactsTable productTemplate={productTemplate} />
      {recommendedProducts?.length > 0 && (
        <RelatedProducts
          productTemplateSlug={slug}
          products={recommendedProducts}
          title={t(`${TP}.recommendations`, 'Recommendations')}
          titleTag="h3"
          section="recommendations"
        />
      )}
      {featuredInCollections?.length > 0 && (
        <FeaturedInProducts
          productTemplateSlug={slug}
          productTemplateId={id}
          featuredInCollections={featuredInCollections.slice(0, 2)}
        />
      )}
      <Footer />
    </ProductDetailPageMobile.Wrapper>
  );
};

const GRADIENT_HEIGHT = `calc(${NAV_PDP_MOBILE_HEADER_HEIGHT} - 1px)`;

ProductDetailPageMobile.Wrapper = styled.div<{ $isBannerVisible: boolean; $innerHeight: number }>`
  .topGradient {
    position: fixed;
    top: ${({ $isBannerVisible }) =>
      $isBannerVisible ? `calc(${MOBILE_BANNER_HEIGHT} + ${GRADIENT_HEIGHT})` : GRADIENT_HEIGHT};
    height: 24px;
    width: 100%;
    z-index: ${({ theme }) => theme.zIndex.mobilePdpHeaderGradient};
    background: linear-gradient(to bottom, white, transparent);
  }

  &.lockScroll {
    position: fixed;
    z-index: ${({ theme }) => theme.zIndex.productDetailPageBuyBarOpen};
  }

  .productInfoFit {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: start;
    margin: 30px 8px 24px 16px;

    .activityAndFitButtons {
      display: flex;
      flex-direction: row;
      align-items: flex-end;
    }
  }

  .wantsButtonWrapper {
    top: ${({ $isBannerVisible }) => ($isBannerVisible ? `${MOBILE_BANNER_HEIGHT}` : 0)};
    position: fixed;
    right: 0;
    padding: 16px;
    z-index: ${({ theme }) => theme.zIndex.pdpVariantWindow};
  }

  .mainView {
    display: flex;
    flex-direction: column;
    height: 100%;

    @media (min-height: ${({ theme }) => theme.breakpoints.small}px) {
      min-height: -moz-available;
      min-height: -webkit-fill-available;
      min-height: fill-available;
      /* this is for devices that do not support 100svh */
      height: ${({ $innerHeight }) => ($innerHeight > 0 ? `${$innerHeight}px` : '100%')};
      height: 100svh;
    }

    /* This is too account for google crawlers which are on a viewport of around 1700px high */
    @media (min-height: ${({ theme }) => theme.breakpoints.medium}px) {
      height: 650px;
    }
  }
`;
